// Plain JS singleton store with EventTarget-based pub/sub.
// Plot shape: { polygonM: [[xM, yM], ...], source, address?, area_m2?, vri?, cadastreNumber? }
// Object shape: { id, type, xM, yM, widthM, heightM, rotationDeg, heightMetresAboveGround?, ... }

import { DEFAULT_SATELLITE_PROVIDER, isSatelliteProvider } from "./satellite_providers"

const MAX_SNAPSHOTS = 50

class EditorStore extends EventTarget {
  constructor() {
    super()
    this.plot = null
    this.objects = []
    this.selectedId = null
    // Insolation state:
    //   dateIso — wall-clock local timestamp at plot (YYYY-MM-DDTHH:mm:ss)
    //   utcOffsetHoursOverride — user override, null = auto-estimate from
    //     plot.originLngLat longitude.
    this.sun = { dateIso: defaultSunIso(), utcOffsetHoursOverride: null }
    // Insolation visualisation mode: "shadows" (live shadow polygons) or
    // "heatmap" (day-total sunlit minutes per grid cell) or "off".
    this.insolationMode = "shadows"
    // Stage 4 — norms validator state.
    //   ruleSetLocale/ruleSetVersion pick the active rule pack (see rule_set.js)
    //   disabledRuleIds stores rule ids the user has unchecked in the panel
    this.norms = {
      ruleSetLocale: "ru",
      ruleSetVersion: "2024-01",
      disabledRuleIds: new Set(),
    }
    // When non-null, the next stage click places a new object of this type
    // at the click point (replaces drag-n-drop on touch devices).
    this.pendingAddType = null
    // Stage 6d + 8a — toggleable UI prefs. Persisted by autosave.
    //   showEdgeDimensions — "Размеры участка" checkbox.
    //   satelliteBg.enabled — "Спутник" toggle (off by default so existing
    //     users don't suddenly see imagery and wonder what changed).
    //   satelliteBg.opacity — 0..1, default 0.9 so neighbour houses, roads,
    //     and vegetation are clearly visible; the plot outline already uses
    //     a white halo + dark dash pair to stay legible on bright imagery.
    //   satelliteBg.provider — imagery source id (see satellite_providers.js);
    //     defaults to Esri (sharper than MapTiler over the ИЖС market).
    this.ui = {
      showEdgeDimensions: true,
      satelliteBg: { enabled: false, opacity: 0.9, provider: DEFAULT_SATELLITE_PROVIDER },
    }
    this._undoStack = []
    this._redoStack = []
  }

  reset() {
    this.plot = null
    this.objects = []
    this.selectedId = null
    this._undoStack = []
    this._redoStack = []
    this.#emit("plot:change")
    this.#emit("selection:change")
  }

  setPlot(plot, { snapshot = false } = {}) {
    if (snapshot) this.pushSnapshot()
    this.plot = plot
    this.#emit("plot:change")
  }

  replacePlot(plot) {
    this.plot = plot
    this.objects = []
    this.selectedId = null
    this._undoStack = []
    this._redoStack = []
    this.#emit("plot:change")
    this.#emit("selection:change")
  }

  // Used by autosave on page load. Restores saved state without going through
  // plot/add/update mutation paths, but still clears history so undo doesn't
  // roll back to an empty editor (unexpected to users).
  restoreDraft({ plot, objects, norms, ui }) {
    // Hint to subscribers (currently autosave) that incoming change events
    // originate from a silent state load, not a user edit — so they shouldn't
    // schedule a save in response. Renderers still react and redraw.
    this._hydrating = true
    this.plot = plot
    this.objects = objects.map(cloneObject)
    this.selectedId = null
    this._undoStack = []
    this._redoStack = []
    if (norms) {
      const disabled = Array.isArray(norms.disabledRuleIds) ? norms.disabledRuleIds : []
      this.norms = {
        ruleSetLocale: norms.ruleSetLocale ?? this.norms.ruleSetLocale,
        ruleSetVersion: norms.ruleSetVersion ?? this.norms.ruleSetVersion,
        disabledRuleIds: new Set(disabled),
      }
      this.#emit("norms:change")
    }
    if (ui) {
      const nextSat = ui.satelliteBg && typeof ui.satelliteBg === "object"
        ? {
            enabled: typeof ui.satelliteBg.enabled === "boolean" ? ui.satelliteBg.enabled : this.ui.satelliteBg.enabled,
            opacity: Number.isFinite(ui.satelliteBg.opacity)
              ? Math.max(0, Math.min(1, ui.satelliteBg.opacity))
              : this.ui.satelliteBg.opacity,
            // Unknown/stale provider ids fall back to the current default so a
            // renamed/removed provider in an old draft can't blank the backdrop.
            provider: isSatelliteProvider(ui.satelliteBg.provider)
              ? ui.satelliteBg.provider
              : this.ui.satelliteBg.provider,
          }
        : this.ui.satelliteBg
      this.ui = {
        ...this.ui,
        showEdgeDimensions: typeof ui.showEdgeDimensions === "boolean" ? ui.showEdgeDimensions : this.ui.showEdgeDimensions,
        satelliteBg: nextSat,
      }
      this.#emit("ui:change")
    }
    this.#emit("plot:change")
    this.#emit("objects:change", { type: "restore" })
    this.#emit("selection:change")
    this._hydrating = false
  }

  addObject(partial) {
    this.pushSnapshot()
    const id = partial.id ?? randomId()
    const obj = { id, rotationDeg: 0, ...partial }
    this.objects.push(obj)
    this.#emit("objects:change", { type: "add", id })
    return obj
  }

  updateObject(id, patch, { snapshot = true } = {}) {
    const idx = this.objects.findIndex((o) => o.id === id)
    if (idx === -1) return null
    if (snapshot) this.pushSnapshot()
    this.objects[idx] = { ...this.objects[idx], ...patch }
    this.#emit("objects:change", { type: "update", id })
    return this.objects[idx]
  }

  removeObject(id) {
    const before = this.objects.length
    this.pushSnapshot()
    this.objects = this.objects.filter((o) => o.id !== id)
    if (this.objects.length !== before) {
      if (this.selectedId === id) {
        this.selectedId = null
        this.#emit("selection:change")
      }
      this.#emit("objects:change", { type: "remove", id })
    }
  }

  duplicateObject(id) {
    const src = this.objects.find((o) => o.id === id)
    if (!src) return null
    // Deep-clone so the copy's houseDesign isn't aliased to the source's.
    const copy = { ...cloneObject(src), id: randomId(), xM: src.xM + 2, yM: src.yM + 2 }
    this.pushSnapshot()
    this.objects.push(copy)
    this.selectedId = copy.id
    this.#emit("objects:change", { type: "add", id: copy.id })
    this.#emit("selection:change")
    return copy
  }

  selectObject(id) {
    if (this.selectedId === id) return
    this.selectedId = id
    this.#emit("selection:change")
  }

  setSunDateIso(iso) {
    if (!iso || this.sun.dateIso === iso) return
    this.sun = { ...this.sun, dateIso: iso }
    this.#emit("sun:change")
  }

  setInsolationMode(mode) {
    if (!["shadows", "heatmap", "off"].includes(mode) || this.insolationMode === mode) return
    this.insolationMode = mode
    this.#emit("insolation:mode-change")
  }

  setRuleDisabled(ruleId, disabled) {
    const set = this.norms.disabledRuleIds
    const was = set.has(ruleId)
    if (disabled) set.add(ruleId)
    else set.delete(ruleId)
    if (was === disabled) return
    this.norms = { ...this.norms, disabledRuleIds: new Set(set) }
    this.#emit("norms:change")
  }

  setPendingAddType(typeId) {
    const next = typeId || null
    if (this.pendingAddType === next) return
    this.pendingAddType = next
    this.#emit("pending-add:change")
  }

  setUiShowEdgeDimensions(flag) {
    const next = !!flag
    if (this.ui.showEdgeDimensions === next) return
    this.ui = { ...this.ui, showEdgeDimensions: next }
    this.#emit("ui:change")
  }

  setUiSatelliteBgEnabled(flag) {
    const next = !!flag
    if (this.ui.satelliteBg.enabled === next) return
    this.ui = { ...this.ui, satelliteBg: { ...this.ui.satelliteBg, enabled: next } }
    this.#emit("ui:change")
  }

  setUiSatelliteBgOpacity(opacity) {
    const next = Math.max(0, Math.min(1, Number(opacity) || 0))
    if (this.ui.satelliteBg.opacity === next) return
    this.ui = { ...this.ui, satelliteBg: { ...this.ui.satelliteBg, opacity: next } }
    this.#emit("ui:change")
  }

  setUiSatelliteBgProvider(provider) {
    // Ignore unknown ids rather than blanking the backdrop with a bad slug.
    if (!isSatelliteProvider(provider) || this.ui.satelliteBg.provider === provider) return
    this.ui = { ...this.ui, satelliteBg: { ...this.ui.satelliteBg, provider } }
    this.#emit("ui:change")
  }

  setRuleSet(locale, version) {
    if (this.norms.ruleSetLocale === locale && this.norms.ruleSetVersion === version) return
    this.norms = { ...this.norms, ruleSetLocale: locale, ruleSetVersion: version }
    this.#emit("norms:change")
  }

  // Manual override for UTC offset at the plot location. When null (default),
  // the editor derives offset from plot.originLngLat's longitude.
  setUtcOffsetHours(hours) {
    const next = hours == null ? null : Math.max(-12, Math.min(14, Math.round(hours * 2) / 2))
    if (this.sun.utcOffsetHoursOverride === next) return
    this.sun = { ...this.sun, utcOffsetHoursOverride: next }
    this.#emit("sun:change")
  }

  // Snapshot-based history. We capture the whole objects array plus
  // selectedId — cheap enough (plain JS objects, <1KB per snapshot even
  // with 50 objects) and trivially correct vs. command-pattern stacks.
  pushSnapshot() {
    this._undoStack.push(this.#snapshot())
    if (this._undoStack.length > MAX_SNAPSHOTS) this._undoStack.shift()
    this._redoStack = []
  }

  canUndo() { return this._undoStack.length > 0 }
  canRedo() { return this._redoStack.length > 0 }

  undo() {
    const snap = this._undoStack.pop()
    if (!snap) return false
    this._redoStack.push(this.#snapshot())
    this.#restore(snap)
    return true
  }

  redo() {
    const snap = this._redoStack.pop()
    if (!snap) return false
    this._undoStack.push(this.#snapshot())
    this.#restore(snap)
    return true
  }

  #snapshot() {
    return {
      // cloneObject deep-clones houseDesign (Stage 9a) so editing a floor plan
      // after pushing a snapshot can't mutate the captured history.
      objects: this.objects.map(cloneObject),
      selectedId: this.selectedId,
      // Deep-clone the polygon so later edits can't reach back into the snapshot.
      plot: this.plot ? { ...this.plot, polygonM: this.plot.polygonM?.map((p) => [p[0], p[1]]) } : null,
    }
  }

  #restore(snap) {
    this.objects = snap.objects.map(cloneObject)
    this.selectedId = snap.selectedId
    const plotChanged = !samePlot(this.plot, snap.plot)
    if (plotChanged) {
      this.plot = snap.plot ? { ...snap.plot, polygonM: snap.plot.polygonM?.map((p) => [p[0], p[1]]) } : null
      this.#emit("plot:change")
    }
    this.#emit("objects:change", { type: "restore" })
    this.#emit("selection:change")
  }

  #emit(name, detail = {}) {
    this.dispatchEvent(new CustomEvent(name, { detail }))
  }
}

// Cheap structural comparison for plot snapshots — avoids an O(n) diff on
// every restore when nothing changed (e.g. an objects-only undo step).
// Also checks originLngLat so an undo that swaps to a differently-located
// plot (same polygon shape, different coordinates) still emits plot:change
// and invalidates the insolation cache (which keys on the origin).
function samePlot(a, b) {
  if (a === b) return true
  if (!a || !b) return false
  if (a.source !== b.source) return false
  const oa = a.originLngLat, ob = b.originLngLat
  if (oa !== ob) {
    if (!oa || !ob || oa[0] !== ob[0] || oa[1] !== ob[1]) return false
  }
  const pa = a.polygonM, pb = b.polygonM
  if (pa === pb) return true
  if (!pa || !pb || pa.length !== pb.length) return false
  for (let i = 0; i < pa.length; i++) {
    if (pa[i][0] !== pb[i][0] || pa[i][1] !== pb[i][1]) return false
  }
  return true
}

// Shallow-clone an object record, deep-cloning its houseDesign (Stage 9a) so
// the floor-plan graph is never shared between live state and a snapshot /
// duplicate / hydrated draft. houseDesign is plain JSON (vertices, walls), so
// structuredClone is exact; JSON round-trip is the fallback for old engines.
function cloneObject(o) {
  const copy = { ...o }
  if (o.houseDesign) copy.houseDesign = cloneHouseDesign(o.houseDesign)
  return copy
}

function cloneHouseDesign(hd) {
  if (!hd) return hd
  if (typeof structuredClone === "function") return structuredClone(hd)
  return JSON.parse(JSON.stringify(hd))
}

// crypto.randomUUID() only works in secure contexts (https / localhost).
// Prefix all ids with "obj-" so they never start with a digit: Konva's
// findOne(`#${id}`) parses the selector as CSS, and ids starting with a digit
// are invalid CSS identifiers and fail silently.
function randomId() {
  const uuid = globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  return `obj-${uuid}`
}

function defaultSunIso() {
  const d = new Date()
  d.setHours(12, 0, 0, 0)
  return isoLocal(d)
}

// ISO 8601 local time (no trailing Z) — matches <input type="datetime-local">.
export function isoLocal(d) {
  const pad = (n) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`
}

export function demoPlot() {
  return {
    source: "demo",
    polygonM: [
      [0, 0], [50, 0], [50, 40], [0, 40], [0, 0]
    ]
  }
}

// One store per tab is fine for Stage 0-2; revisit if editor gets multi-project tabs.
export const editorStore = new EditorStore()
